/**
 * CreateMenu — the global create the product did not have.
 *
 * One gold button, one list of the five things a person can make here, reachable
 * from the shell and from ⌘K. Both entry points read the same array, so the
 * palette can never offer a verb the button does not, or name it differently.
 *
 * On the gold. `--accent` is `#d9a441` in dark and `#b07e28` in light, and the
 * light value was called muddy at button size. Measured:
 *
 *   dark    #040c17 on #d9a441   8.73:1 label · fill vs canvas 8.54:1
 *   light   #2a1c02 on #b07e28   4.63:1 label · fill vs canvas 3.31:1
 *   light   #2a1c02 on #d9a441   7.38:1 label · fill vs canvas 2.25:1  ✗
 *   light   #ffffff on #b07e28   3.58:1 label                          ✗
 *
 * So the pairing stays `--accent` + `--text-on-accent` (Button's `primary`): it
 * is the only one whose *shape* clears 3:1 against the light canvas, which is
 * what makes a filled button read as a button. Brightening the light fill to the
 * dark theme's gold would buy label contrast and lose the button's edge — it
 * would float on white. What the light theme needs instead is weight: the label
 * runs semibold here, because 4.63:1 at 13px medium is where "muddy" comes from.
 * Everything else — radius, height, icon, word — is identical in both themes.
 */

import { Bookmark, Building2, ChevronDown, Inbox, ListChecks, Plus, UserRound } from 'lucide-react';
import { createElement, type ReactNode } from 'react';

import { cn } from '@/lib/cn';
import type { IconComponent } from '@/lib/constants';
import { Button, type ButtonVariant, type ControlSize } from './Button';
import { Menu, type MenuItem } from './Menu';

export type CreateKind = 'inquiry' | 'contact' | 'company' | 'task' | 'view';

export interface CreateAction {
  kind: CreateKind;
  /** The noun alone. The menu's heading supplies the verb. */
  label: string;
  /** One clause on what it is for. Also the ⌘K result's second line. */
  description: string;
  icon: IconComponent;
}

/**
 * The canonical create vocabulary. Order is deliberate: it runs from the thing
 * the pipeline is made of outward to the thing that organises the pipeline.
 */
export const CREATE_ACTIONS: readonly CreateAction[] = [
  {
    kind: 'inquiry',
    label: 'Inquiry',
    description: 'Log a lead that came in off-channel',
    icon: Inbox,
  },
  {
    kind: 'contact',
    label: 'Contact',
    description: 'A person at a company',
    icon: UserRound,
  },
  {
    kind: 'company',
    label: 'Company',
    description: 'An organisation inquiries hang off',
    icon: Building2,
  },
  {
    kind: 'task',
    label: 'Task',
    description: 'Something owed to a record',
    icon: ListChecks,
  },
  {
    kind: 'view',
    label: 'View',
    description: 'Save the current filters as a list',
    icon: Bookmark,
  },
] as const;

export interface CreateMenuItemOptions {
  /** Restrict and reorder the offer. Defaults to all five, in canonical order. */
  kinds?: readonly CreateKind[];
  /** Kinds the current role or screen cannot create right now. */
  disabledKinds?: readonly CreateKind[];
  /** `true` in ⌘K, where every result already carries a description. */
  withDescriptions?: boolean;
}

/**
 * The same items the button shows, as `MenuItem[]` — so the command palette
 * mounts one array rather than retyping the list. Exported for the shell.
 */
export function createMenuItems(
  onCreate: (kind: CreateKind) => void,
  options: CreateMenuItemOptions = {},
): MenuItem[] {
  const { kinds, disabledKinds, withDescriptions = false } = options;
  const chosen = kinds
    ? kinds
        .map((kind) => CREATE_ACTIONS.find((action) => action.kind === kind))
        .filter((action): action is CreateAction => action !== undefined)
    : CREATE_ACTIONS;

  return chosen.map((action) => ({
    id: `create-${action.kind}`,
    type: 'item',
    label: action.label,
    description: withDescriptions ? action.description : undefined,
    // A rendered element, not the component: a lucide icon is a `forwardRef`
    // exotic object, so `typeof icon === 'function'` — the branch that would size
    // it for us — is false and the slot would come out empty.
    icon: createElement(action.icon, { className: 'size-4', 'aria-hidden': true }),
    disabled: disabledKinds?.includes(action.kind),
    onSelect: () => onCreate(action.kind),
  }));
}

export interface CreateMenuProps extends CreateMenuItemOptions {
  onCreate: (kind: CreateKind) => void;
  /** The button's word. `New` everywhere unless a surface has a better verb. */
  label?: string;
  size?: ControlSize;
  align?: 'start' | 'center' | 'end';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** Collapsed shell: icon only, same menu. */
  iconOnly?: boolean;
  /**
   * The trigger's treatment. `primary` — gold — is the default and is what a
   * *screen's* create wears.
   *
   * The app shell passes `outline`, because a screen may only carry one
   * `--accent` fill: with the chrome's `New` and a list toolbar's own primary
   * both gold, both `Plus`, both semibold and both on the right edge 96px
   * apart, the eye reads one control rendered twice and neither is the
   * unmistakable primary. The menu behind it is identical either way.
   */
  variant?: ButtonVariant;
}

export function CreateMenu({
  onCreate,
  label = 'New',
  size = 'md',
  align = 'end',
  open,
  onOpenChange,
  className,
  iconOnly = false,
  variant = 'primary',
  ...itemOptions
}: CreateMenuProps): ReactNode {
  const items: MenuItem[] = [
    { id: 'create-heading', type: 'label', label: 'Create' },
    ...createMenuItems(onCreate, itemOptions),
  ];

  return (
    <Menu
      items={items}
      align={align}
      side="bottom"
      label="Create"
      width={224}
      open={open}
      onOpenChange={onOpenChange}
      trigger={
        <Button
          variant={variant}
          size={size}
          aria-label={iconOnly ? `Create — ${label}` : undefined}
          iconLeft={<Plus aria-hidden strokeWidth={2.5} />}
          iconRight={
            iconOnly ? undefined : (
              <ChevronDown
                aria-hidden
                className="transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)] group-data-[state=open]/create:rotate-180"
              />
            )
          }
          className={cn(
            // The light theme's gold is a darker, lower-contrast fill than the
            // dark theme's; the label carries the difference in weight so the
            // two read as one button. See the note at the top of this file.
            'group/create shrink-0 font-semibold',
            variant === 'primary' && 'data-[state=open]:bg-accent-hover',
            className,
          )}
        >
          {iconOnly ? null : label}
        </Button>
      }
    />
  );
}
